/**
 * Load the native viewer on demand after backend feature enablement.
 * Row buttons are local; the SDK is shared by clicks and login-return restoration.
 */

import { BASENAME, URL as APP_URL } from './constants/general';
import { adobSnapMagicLog } from './analytics/adobe/adob';
import { localSnapMagicLog } from './analytics/local';

export const SNAPMAGIC = {
	TOKEN: process.env.REACT_APP_SNAPMAGIC_TOKEN || '1f41d58382ceaa99ddf3af6358483d',
	HOST: 'https://www.snapeda.com',
	BASE_SRC: 'https://www.snapeda.com/distributor/files/plugins/snapeda/snapeda.min.js',
	// SnapEDA indexes onsemi parts under several manufacturer names (legacy
	// "ON Semiconductor" and post-2021-rebrand "onsemi"); the plugin tries each.
	MANUFACTURER_ALIASES: 'onsemi,ON Semiconductor,Onsemi'
};

/** MyON sign-in URL that returns the user to their current SPA route. */
export function buildMyOnLoginUrl() {
	const returnUrl = typeof window !== 'undefined' ? window.location.href : '';
	return `${APP_URL.MYON_LOGIN}?returnUrl=${encodeURIComponent(returnUrl)}`;
}

const HOST_ID = 'snapmagic-shared-host';
const STYLE_ID = 'snapmagic-shared-style';
const SCRIPT_ATTR = 'data-snapmagic-shared';
const RESTORE_STATE_KEY = 'snapmagic-restore-state';

const POLL_MS = 200;
const VIEWER_OPEN_TIMEOUT_MS = 60000;

let featureConfigPromise;
let featureEnabled = false;
let myOnSessionPromise;

export function isSnapMagicEnabled() {
	if (!featureConfigPromise) {
		featureConfigPromise = fetch(`${BASENAME}/api/features`, { cache: 'no-store' })
			.then(response => (response.ok ? response.json() : null))
			.then(config => config?.snapmagicEnabled === true)
			.catch(() => false)
			.then(enabled => {
				featureEnabled = enabled;
				if (!enabled) window.sessionStorage.removeItem(RESTORE_STATE_KEY);
				return enabled;
			});
	}
	return featureConfigPromise;
}

function getMyOnSession() {
	if (!myOnSessionPromise) {
		myOnSessionPromise = fetch('/user/get-current', { credentials: 'include' })
			.then(response => (response.status === 200 ? response.json() : null))
			.catch(() => null);
	}
	return myOnSessionPromise;
}

const viewerContexts = new WeakMap();
let listenersRegistered = false;
let activeViewer;
let restorePageStateProvider;
let pluginScriptPromise;
let openingInProgress = false;
let partRequestPending = false;

// Emitted element values documented by SnapMagic. e.detail.part_id is an
// internal model ID; e.detail.part contains the manufacturer part number.
// Event names are prefixed with PRT_ to distinguish them from onsemi-web events in analytics.
const SNAPMAGIC_EVENTS = {
	'viewer-open': 'PRT_SnapMag_viewer_open',
	'download-3d-click': 'PRT_SnapMag_download_3d_cad_model',
	'download-2d-format-click': 'PRT_SnapMag_download_symbol_or_footprint',
	'login-click': 'PRT_SnapMag_login',
	'request-submitted': 'PRT_SnapMag_request_model'
};
const RESTORE_EVENTS = new Set(['download-2d-click', 'download-3d-click', 'request-cad-click', 'login-click']);

function trackSnapMagicEvent(eventName, { opn, element, partId } = {}) {
	const currentPrt = typeof window !== 'undefined' ? window.location.href : '';
	const trackingParameters = { eventName, opn, currentPrt, element, partId };
	Promise.allSettled([
		localSnapMagicLog(trackingParameters),
		adobSnapMagicLog(trackingParameters).catch(error => {
			console.warn('SnapMagic Adobe tracking failed', eventName, error);
		})
	]);
}

function resolveViewerContext(viewer) {
	try {
		const target = typeof viewer === 'string' ? document.querySelector(viewer) : viewer;
		return target?.isConnected ? viewerContexts.get(target) : undefined;
	} catch (_error) {
		return undefined;
	}
}

function handleSnapMagicEvent(event) {
	const { element, part, viewer, part_id: partId } = event.detail || {};
	if (typeof element !== 'string') return;
	const context = resolveViewerContext(viewer);
	if (!context) return;
	const opn = typeof part === 'string' && part.trim() ? part : context.part;

	if (element === 'modal-close') {
		if (activeViewer?.target === context.target) activeViewer = undefined;
		return;
	}
	if (element === 'viewer-open') {
		activeViewer = { ...context, part: opn };
		if (!context.trackViewerOpen) {
			context.trackViewerOpen = true;
			return;
		}
	}
	if (!context.isCustomerLoggedIn && RESTORE_EVENTS.has(element)) {
		saveRestoreState({ part: opn, lang: context.lang });
	}
	const eventName = SNAPMAGIC_EVENTS[element];
	if (typeof eventName === 'string') trackSnapMagicEvent(eventName, { opn, element, partId });
}

function updateSnapMagicLoginLink(event) {
	const loginLink = event.target.closest?.('a[href]');
	if (!loginLink) return;
	const target = event.composedPath().find(node => viewerContexts.has(node));
	const context = resolveViewerContext(target);
	if (!context || context.isCustomerLoggedIn) return;
	const loginUrl = buildMyOnLoginUrl();
	if (
		loginLink.getAttribute('data-track-dispatch-event') !== 'login-click' &&
		!loginLink.href.startsWith(new URL(loginUrl, window.location.href).href.split('?')[0])
	) {
		return;
	}
	loginLink.href = loginUrl;
	saveRestoreState(activeViewer?.target === target ? activeViewer : context);
}

function registerViewerContext({ target, part, lang, isCustomerLoggedIn, trackViewerOpen = true }) {
	viewerContexts.set(target, { target, part, lang, isCustomerLoggedIn, trackViewerOpen });
	if (listenersRegistered) return;
	document.addEventListener('snapedaclick', handleSnapMagicEvent);
	document.addEventListener('click', updateSnapMagicLoginLink, true);
	listenersRegistered = true;
}

export function getCurrentPagePath() {
	return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function saveRestoreState({ part, lang }) {
	if (typeof window === 'undefined') return;
	window.sessionStorage.setItem(
		RESTORE_STATE_KEY,
		JSON.stringify({
			part,
			lang,
			path: getCurrentPagePath(),
			scrollX: window.scrollX,
			scrollY: window.scrollY,
			pageState: restorePageStateProvider?.() ?? null
		})
	);
}

/** Registers the product-page state that should survive a MyON login redirect. */
export function setSnapMagicRestorePageStateProvider(provider) {
	restorePageStateProvider = provider;
	return () => {
		if (restorePageStateProvider === provider) restorePageStateProvider = null;
	};
}

function readSnapMagicRestoreState() {
	if (typeof window === 'undefined' || !featureEnabled) return null;

	const savedState = window.sessionStorage.getItem(RESTORE_STATE_KEY);
	if (!savedState) return null;

	try {
		const state = JSON.parse(savedState);
		const path = state.path || state.pathname;
		return path && state.part ? { ...state, path } : null;
	} catch (_error) {
		return null;
	}
}

/** Returns the PRT+ route to restore after a MyON redirect, if one was saved. */
export function getSnapMagicRestorePath() {
	return readSnapMagicRestoreState()?.path ?? null;
}

export function consumeSnapMagicRestoreState() {
	if (typeof window === 'undefined') return null;

	const state = readSnapMagicRestoreState();
	if (!state || state.path !== getCurrentPagePath()) return null;

	window.sessionStorage.removeItem(RESTORE_STATE_KEY);
	return state;
}

/** Create the shared host used by the SnapMagic plugin and its popup. */
function ensureSharedHost() {
	let host = document.getElementById(HOST_ID);
	if (!host) {
		host = document.createElement('div');
		host.id = HOST_ID;
		// Keep the plugin host in the DOM without affecting table layout.
		Object.assign(host.style, {
			position: 'fixed',
			left: '0',
			top: '0',
			width: '0',
			height: '0',
			zIndex: '10009'
		});
		document.body.appendChild(host);
	}

	if (!document.getElementById(STYLE_ID)) {
		const style = document.createElement('style');
		style.id = STYLE_ID;
		style.textContent =
			`#${HOST_ID} #btn-popup-modal{position:absolute!important;width:1px!important;` +
			`height:1px!important;overflow:hidden!important;clip:rect(0,0,0,0)!important;}`;
		document.head.appendChild(style);
	}

	return host;
}

function loadPluginScript({ lang, host, token, isCustomerLoggedIn, loginUrl }) {
	if (pluginScriptPromise) return pluginScriptPromise;

	const script = document.createElement('script');
	pluginScriptPromise = new Promise((resolve, reject) => {
		script.src = `${SNAPMAGIC.BASE_SRC}?token=${token}`;
		Object.entries({
			[SCRIPT_ATTR]: '1',
			token,
			host,
			lang,
			isCustomerLoggedIn: isCustomerLoggedIn ? '1' : '0',
			LoginUrl: loginUrl
		}).forEach(([name, value]) => script.setAttribute(name, value));
		script.onload = () => {
			if (typeof window.SNAPEDA?.init === 'function') resolve(window.SNAPEDA);
			else reject(new Error('SnapMagic initialization API is not available'));
		};
		script.onerror = reject;
		document.head.appendChild(script);
	}).catch(error => {
		script.remove();
		pluginScriptPromise = null;
		throw error;
	});
	return pluginScriptPromise;
}

/**
 * Wait for the plugin's trigger, open it, and report success or timeout.
 */
function watchAndOpen(onSettled, attempt) {
	let triggerClicked = false;

	const poll = () => {
		if (!attempt.active) return;
		const host = document.getElementById(HOST_ID);
		if (!host || Date.now() > attempt.deadline) {
			onSettled(false);
			return;
		}
		if (host.querySelector('.snapeda-plugin-modal.md-dialog-visible')) {
			onSettled(true);
			return;
		}

		const button = host.querySelector('#btn-popup-modal');
		if (button && typeof window.jQuery === 'function' && !triggerClicked) {
			triggerClicked = true;
			button.click();
		}
		setTimeout(poll, POLL_MS);
	};

	poll();
}

function initializeViewer(snapeda, part, manufacturer, attempt, onSettled) {
	const getPart = snapeda.getPart;
	if (typeof getPart === 'function') {
		snapeda.getPart = async function (...args) {
			partRequestPending = true;
			try {
				for (let request = 0; request < 2; request++) {
					let data;
					try {
						data = await getPart.apply(this, args);
					} catch (error) {
						const status = error?.status;
						const retryable = status === 0 || status === 408 || (status >= 500 && status < 600);
						if (request === 0 && retryable && attempt.active && Date.now() <= attempt.deadline) continue;
						throw error;
					}
					if (!attempt.active || Date.now() > attempt.deadline) throw new Error('SnapMagic request expired');
					if (!data?.status) onSettled(false);
					return data;
				}
			} catch (error) {
				onSettled(false);
				throw error;
			} finally {
				partRequestPending = false;
			}
		};
	}
	try {
		snapeda.init(part, manufacturer, `#${HOST_ID}`);
	} finally {
		if (typeof getPart === 'function') snapeda.getPart = getPart;
	}
}

/**
 * Open the shared viewer for an OPN or WPN. onSettled reports whether it opened.
 */
export async function openSnapMagicModal({
	part,
	manufacturer = SNAPMAGIC.MANUFACTURER_ALIASES,
	lang = 'en',
	host = SNAPMAGIC.HOST,
	token = SNAPMAGIC.TOKEN,
	trackViewerOpen = true,
	onSettled = () => {}
} = {}) {
	if (!(await isSnapMagicEnabled())) {
		onSettled(false);
		return;
	}
	if (
		!part ||
		openingInProgress ||
		partRequestPending ||
		document.getElementById(HOST_ID)?.querySelector('.snapeda-plugin-modal.md-dialog-visible')
	) {
		onSettled(false);
		return;
	}

	openingInProgress = true;
	const attempt = { active: true, deadline: 0 };
	const settle = opened => {
		if (!attempt.active) return;
		attempt.active = false;
		openingInProgress = false;
		if (!opened) viewerContexts.delete(document.getElementById(HOST_ID));
		onSettled(opened);
	};
	const user = await getMyOnSession();
	const isLoggedIn = user !== null;
	const loginUrl = buildMyOnLoginUrl();
	const sharedHost = ensureSharedHost();
	registerViewerContext({ target: sharedHost, part, lang, isCustomerLoggedIn: isLoggedIn, trackViewerOpen });
	try {
		const snapeda = await loadPluginScript({
			lang,
			host,
			token,
			isCustomerLoggedIn: isLoggedIn,
			loginUrl
		});
		attempt.deadline = Date.now() + VIEWER_OPEN_TIMEOUT_MS;
		initializeViewer(snapeda, part, manufacturer, attempt, settle);
	} catch (_error) {
		settle(false);
		return;
	}
	watchAndOpen(settle, attempt);
}
